import { Box, Heading, HStack, Badge, Text, Wrap, WrapItem } from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import type { ContactDetail } from '../../types/contactDetail'
import { FLAG_BADGES, flagLabel } from '../../types/analyticsFlags'
import { FlagLegend } from './FlagLegend'

interface ContactProfileHeaderProps {
  contact: ContactDetail
}

const statusColors: Record<string, string> = {
  NotContacted: 'gray',
  Contacted: 'blue',
  Interested: 'green',
  NotInterested: 'red',
  NoAnswer: 'orange',
  Callback: 'yellow',
}

const tagTypeColors: Record<string, string> = {
  Volunteer: 'green',
  Donor: 'purple',
  Activist: 'orange',
  Issue: 'blue',
}

export function ContactProfileHeader({ contact }: ContactProfileHeaderProps) {
  return (
    <Box bg="surface" p={6} borderRadius="lg" shadow="md">
      <HStack justify="space-between" align="start" mb={3}>
        <Box>
          <Heading size="lg">{contact.fullName}</Heading>
          <HStack gap={4} mt={1}>
            {contact.age && (
              <Text fontSize="sm" color="gray.600">Age: {contact.age}</Text>
            )}
            {contact.voterFileId && (
              <Text fontSize="sm" color="gray.600">Voter-File ID: {contact.voterFileId}</Text>
            )}
            {contact.territoryName && (
              <Text fontSize="sm" color="gray.600">Territory: {contact.territoryName}</Text>
            )}
          </HStack>
        </Box>
        <HStack>
          <Badge
            colorPalette={statusColors[contact.status] ?? 'gray'}
            fontSize="sm"
            px={3}
            py={1}
            borderRadius="md"
          >
            {contact.status}
          </Badge>
          {!contact.isActive && (
            <Badge colorPalette="red" fontSize="sm" px={3} py={1} borderRadius="md">
              Inactive
            </Badge>
          )}
        </HStack>
      </HStack>

      {contact.tags.length > 0 && (
        <Wrap mt={3}>
          {contact.tags.map((tag) => (
            <WrapItem key={tag.id}>
              <Badge
                colorPalette={tagTypeColors[tag.tagType ?? ''] ?? 'gray'}
                variant="subtle"
                px={2}
                py={0.5}
                borderRadius="full"
              >
                {tag.label}
              </Badge>
            </WrapItem>
          ))}
        </Wrap>
      )}

      {(contact.analyticsFlags?.length ?? 0) > 0 && (
        <Wrap mt={2}>
          {contact.analyticsFlags.map((flag) => (
            <WrapItem key={flag.flagKey}>
              <Tooltip content={flag.explanation} showArrow>
                <Badge
                  colorPalette={FLAG_BADGES[flag.flagKey]?.colorScheme ?? 'gray'}
                  variant="outline"
                  px={2}
                  py={0.5}
                  borderRadius="full"
                >
                  {flagLabel(flag.flagKey)}
                </Badge>
              </Tooltip>
            </WrapItem>
          ))}
        </Wrap>
      )}

      <FlagLegend
        flags={contact.analyticsFlags ?? []}
        impactScores={contact.impactScores ?? []}
      />
    </Box>
  );
}
